import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { Download, Share2, X, Sparkles } from "lucide-react";
import { TeacherAvatar } from "./TeacherAvatar";

export const BadgeCelebration = ({ badges, studentName, language, api, authConfig, onClose }) => {
  const primary = badges[0];
  const audioRef = useRef(null);

  useEffect(() => {
    const bursts = [0, 400, 900];
    bursts.forEach((delay, i) =>
      setTimeout(() => confetti({
        particleCount: 90, spread: 75 + i * 15, startVelocity: 38,
        origin: { x: 0.3 + i * 0.2, y: 0.55 },
        colors: ["#00F0FF", "#22D3EE", "#7000FF", "#fbbf24", "#f472b6"],
        zIndex: 9999,
      }), delay)
    );
    fetch(`${api}/tutor/tts`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(authConfig.headers || {}) },
      credentials: authConfig.withCredentials ? "include" : "same-origin",
      body: JSON.stringify({ text: language === "tamil"
        ? `வாழ்த்துகள் ${studentName}! நீங்கள் ${primary.label} பேட்ஜ் பெற்றுவிட்டீர்கள்! உங்களைப் பற்றி நான் மிகவும் பெருமைப்படுகிறேன். இப்படியே தொடருங்கள்!`
        : `Congratulations ${studentName}! You earned the ${primary.label} badge! I am so proud of you — keep it up!` }),
    }).then((r) => (r.ok ? r.blob() : null)).then((b) => {
      if (!b) return;
      const audio = new Audio(URL.createObjectURL(b));
      audioRef.current = audio;
      audio.play().catch(() => {});
    }).catch(() => {});
    return () => { if (audioRef.current) audioRef.current.pause(); };
    // eslint-disable-next-line
  }, []);

  function drawCard() {
    const c = document.createElement("canvas");
    c.width = 900; c.height = 520;
    const ctx = c.getContext("2d");
    const grad = ctx.createLinearGradient(0, 0, 900, 520);
    grad.addColorStop(0, "#031419"); grad.addColorStop(1, "#12032b");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, 900, 520);
    ctx.strokeStyle = "#22D3EE"; ctx.lineWidth = 5;
    ctx.strokeRect(14, 14, 872, 492);
    ctx.font = "90px serif"; ctx.textAlign = "center";
    ctx.fillText("🏅", 450, 150);
    ctx.fillStyle = "#22D3EE"; ctx.font = "bold 52px Arial";
    ctx.fillText(primary.label, 450, 235);
    ctx.fillStyle = "#e5e5e5"; ctx.font = "26px Arial";
    ctx.fillText(primary.desc, 450, 285);
    ctx.fillStyle = "#fbbf24"; ctx.font = "bold 32px Arial";
    ctx.fillText(`Earned by ${studentName}`, 450, 360);
    ctx.fillStyle = "#71717a"; ctx.font = "22px Arial";
    ctx.fillText("ShansoTutor — my AI tuition teacher · shansoai.com/tutor", 450, 460);
    return c;
  }

  function downloadCard() {
    const a = document.createElement("a");
    a.href = drawCard().toDataURL("image/png");
    a.download = `${studentName}-${primary.id}-badge.png`;
    a.click();
  }

  async function shareCard() {
    try {
      const blob = await new Promise((res) => drawCard().toBlob(res, "image/png"));
      const file = new File([blob], "badge.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "New badge on ShansoTutor!", text: `${studentName} earned the ${primary.label} badge on ShansoTutor 🎉` });
        return;
      }
    } catch {}
    downloadCard();
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4" data-testid="badge-celebration">
      <div className="w-full max-w-md bg-[#0A0A0A] border border-amber-400/40 rounded-3xl p-6 text-center relative overflow-hidden">
        <button onClick={onClose} data-testid="celebration-close-btn" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18}/></button>
        <div className="flex justify-center mb-1">
          <TeacherAvatar mood="celebrating" size={110}/>
        </div>
        <p className="text-amber-300 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1.5 mb-1">
          <Sparkles size={13}/> New badge unlocked!
        </p>
        <h3 className="text-2xl font-black text-white mb-1" data-testid="celebration-badge-label">{primary.label}</h3>
        <p className="text-zinc-400 text-sm mb-3">{primary.desc}</p>
        {badges.length > 1 && (
          <div className="flex flex-wrap justify-center gap-1.5 mb-3" data-testid="celebration-extra-badges">
            {badges.slice(1).map((b) => (
              <span key={b.id} className="px-2.5 py-1 rounded-full text-[11px] bg-amber-500/10 border border-amber-400/30 text-amber-200">+ {b.label}</span>
            ))}
          </div>
        )}
        <p className="text-cyan-200/90 text-sm italic mb-5">"Shabash, {studentName}! Show this to your parents!" — Shanso Ma'am</p>
        <div className="flex justify-center gap-2 flex-wrap">
          <button onClick={shareCard} data-testid="celebration-share-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-black"
            style={{ background: "linear-gradient(135deg, #fbbf24, #f59e0b)" }}>
            <Share2 size={14}/> Share card
          </button>
          <button onClick={downloadCard} data-testid="celebration-download-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border border-zinc-700 text-zinc-200 hover:border-cyan-400">
            <Download size={14}/> Download
          </button>
        </div>
      </div>
    </div>
  );
};
